'use client';

import { ArrowLeft, Copy, Info, Mail } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type ReactElement, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Link } from '@/i18n/routing';

interface ClaimThirdPartyNoticeCardProps {
  professionalId: string;
  professionalName: string;
  /** Unmasked SAK-registry email, already shown elsewhere on this page
      (ProfileBadgeCard) — SAK-registry addresses are public data. Used only
      to prefill a mailto: the visitor sends themselves; Lexomat never sends
      anything on this branch. */
  professionalEmail: string | null;
}

/** Seconds the "Copied" confirmation stays visible after a copy-link click. */
const COPY_CONFIRMATION_SECONDS = 3;

/**
 * Shown after a visitor declares (via ClaimIdentityGateModal) that they are
 * NOT the professional (#8306). No verification email was sent — this card
 * gives them a way to forward the claim link to the actual lawyer
 * themselves instead, via their own mail client (never Lexomat's).
 */
export function ClaimThirdPartyNoticeCard({
  professionalId,
  professionalName,
  professionalEmail,
}: ClaimThirdPartyNoticeCardProps): ReactElement {
  const t = useTranslations('Lawyers.claim');
  const [copied, setCopied] = useState(false);

  const claimUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}`
      : '';

  async function handleCopyLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(claimUrl);
      setCopied(true);
      window.setTimeout(
        () => setCopied(false),
        COPY_CONFIRMATION_SECONDS * 1000
      );
    } catch {
      // Clipboard API can fail (permissions, insecure context) — the link
      // is also visible as plain text below, so there's a fallback in view.
    }
  }

  const mailtoHref = professionalEmail
    ? `mailto:${professionalEmail}?subject=${encodeURIComponent(
        t('thirdPartyEmailSubject')
      )}&body=${encodeURIComponent(
        t('thirdPartyEmailBody', { link: claimUrl })
      )}`
    : null;

  return (
    <main className='min-h-[80vh] px-4 py-10 sm:px-6 lg:px-8'>
      <div className='mx-auto w-full max-w-xl'>
        <Card className='overflow-hidden border-line-default bg-surface-subtle shadow-sm'>
          <div className='h-1 bg-gradient-to-r from-brand-tertiary via-status-info to-brand-tertiary' />

          <div className='flex flex-col items-center px-6 py-10 text-center sm:px-10'>
            <div className='mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-tertiary/25 to-status-info/25 ring-1 ring-brand-tertiary/40 shadow-sm'>
              <Info className='h-8 w-8 text-brand-tertiary' />
            </div>

            <h1 className='text-2xl font-semibold tracking-tight text-content-primary'>
              {t('thirdPartyNoticeTitle')}
            </h1>

            <p className='mt-3 max-w-sm text-sm text-content-secondary'>
              {t('thirdPartyNoticeDescription', { name: professionalName })}
            </p>

            <div className='mt-6 flex w-full items-center gap-2 rounded-lg border border-line-default bg-surface-muted/50 px-3 py-2 text-left'>
              <span className='min-w-0 flex-1 truncate font-mono text-xs text-content-secondary'>
                {claimUrl}
              </span>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={handleCopyLink}
              >
                <Copy className='mr-1.5 h-3.5 w-3.5' />
                {copied ? t('thirdPartyLinkCopied') : t('thirdPartyCopyLink')}
              </Button>
            </div>

            {mailtoHref ? (
              <a
                href={mailtoHref}
                className='mt-3 inline-flex h-10 items-center justify-center gap-1.5 rounded-md px-4 text-sm font-medium text-brand-tertiary transition-colors hover:text-brand-tertiary-hover'
              >
                <Mail className='h-4 w-4' />
                {t('thirdPartyEmailButton')}
              </a>
            ) : null}

            <Link
              href={{
                pathname: '/advokati/[id]',
                params: { id: professionalId },
              }}
              className='mt-8 inline-flex items-center gap-1.5 text-sm text-content-secondary transition-colors hover:text-brand-tertiary'
            >
              <ArrowLeft className='h-4 w-4' />
              {t('backToProfile')}
            </Link>
          </div>
        </Card>
      </div>
    </main>
  );
}
